<template>
    <div class="container-fluid content-wrapper">
        <div class="content-heading">Recalculate Costs</div>
        <p-form ref="form">
            <p-card>
                <b-overlay :show="recalculatingAccountingCosts">
                    <template v-slot:overlay>
                        <div class="text-center text-primary my-2">
                            <b-spinner variant="primary" class="align-middle"></b-spinner>
                            <strong class="ms-2">{{ spinnerMessage }}</strong>
                        </div>
                    </template>
                    <b-row>
                        <b-col>
                             <div class="mb-2">
                                Recalculate accounting cost for all stock on hand at current location.
                            </div>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <b-button variant="outline-primary" @click="previewAccountingCosts" class="me-2">
                                Preview Exceptions
                            </b-button>
                            <b-button variant="primary" v-b-modal.recalculateAccountingCostModal>
                                Apply Accounting Costs
                            </b-button>
                        </b-col>
                    </b-row>
                </b-overlay>
            </p-card>
            <p-card v-if="explanationMessage">
                <b-row class="mt-3 font-weight-bold">
                    <b-col>
                        <div class="mb-2">{{ explanationMessage }}</div>
                    </b-col>
                </b-row>
                <b-row class="mt-3">
                    <b-col>
                       <b-table
                            v-if="accountingCosts && accountingCosts.length > 0"
                            :items="accountingCosts"
                            :fields="fields"
                            :per-page="perPage"
                            :current-page="currentPage"
                            must-sort
                            striped
                            hover
                            small
                            responsive
                        />
                        <div class="d-flex align-items-center justify-content-end my-2">
                            <b-form-group
                                v-if="accountingCosts && accountingCosts.length > 0"
                                label="Show"
                                label-for="per-page-select"
                                label-cols-sm="auto"
                                class="mb-0 me-3"
                            >
                                <b-form-select
                                    id="per-page-select"
                                    v-model="perPage"
                                    :options="[10, 25, 50, 100, 500]"
                                    size="sm"
                                />
                            </b-form-group>
                            <span v-if="accountingCosts && accountingCosts.length > 0" class="me-3">
                                of {{ accountingCosts.length }}
                            </span>
                            <b-pagination
                                v-if="accountingCosts && accountingCosts.length > 0"
                                v-model="currentPage"
                                :total-rows="accountingCosts.length"
                                :per-page="perPage"
                                align="right"
                                class="mb-0"
                            />
                        </div>
                    </b-col>
                </b-row>
            </p-card>
        </p-form>

        <p-modal size="md" ref="recalculateAccountingCostModal" name="recalculateAccountingCostModal" label="Recalculate Accounting Costs" hide-footer>
            <b-row class="mb-3">
                <b-col>
                    <div class="mb-2" style="min-height: 2em;">
                        Please confirm that you would like to recalculate the accounting costs for all SKUs that currently have stock at the current location.  This operation may take some time and is not reversible.
                        When the process is complete there will be a CSV report containing the results of the recalculation.
                    </div>
                </b-col>
            </b-row>
            <b-row>
                <b-col class="d-flex justify-content-center">
                    <b-button variant="btn btn-outline-primary" class="mx-2" @click="recalculateAccountingCosts()">Recalculate</b-button>
                    <b-button variant="btn btn-outline-primary" class="mx-2" @click="$refs.recalculateAccountingCostModal.hide()">Cancel</b-button>
                </b-col>
            </b-row>
        </p-modal>   
    </div>
</template>

<script>
import axios from 'axios';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';

import {
    currencyFormatter,
    // datetimeToLocalTimeFormatter,
} from '../../components/Common/Formatters.js';

export default {
    data() {
        return {
            explanationMessage: '',
            spinnerMessage: '',
            accountingCosts: [],
            recalculatingAccountingCosts: false,
            perPage: 100,
            currentPage: 1,            
            filters: {
                locationId: null,
                sortBy: 'sku',
                sortDesc: false,
            },            
            fields: [
                {
                    key: 'sku',
                    label: 'SKU',
                    sortable: true
                },
               {
                    key: 'isbn',
                    label: 'ISBN',
                    sortable: true
                },                
                {
                    key: 'dcc',
                    label: 'DCC',
                    sortable: true
                },
                {
                    key: 'description',
                    label: 'Description'
                },
                {
                    key: 'variant1',
                    label: 'Variant 1'
                },
                {
                    key: 'variant2',
                    label: 'Variant 2'
                },
                {
                    key: 'stockOnHand',
                    label: 'Stock On Hand',
                    sortable: true
                },
                {
                    key: 'accountingCost',
                    label: 'Accounting Cost',
                    formatter: (value) => value == null ? 'N/A' : currencyFormatter(value),
                    sortable: true
                },
                {
                    key: 'retail',
                    label: 'Retail',
                    formatter: (value) => value == null ? 'N/A' : currencyFormatter(value),
                    sortable: true
                },                
                {
                    key: 'location',
                    label: 'Location'
                },
                {
                    key: 'calculable',
                    label: 'Calculable',
                    formatter: value => value ? 'Yes' : 'No',
                    sortable: true
                },
                {
                    key: 'isActive',
                    label: 'Active',
                    formatter: value => value ? 'Yes' : 'No',
                    sortable: true
                }                                   
            ]
        };
    },
    mounted : async function() {

    },
    computed: {
    },
    methods: {
        async previewAccountingCosts() {
            this.spinnerMessage = 'Generating preview, please wait...';
            this.recalculatingAccountingCosts = true;
            const locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();            
            axios
                .get('/stockledgers/previewcalculateaccountingcosts', 
                {
                    params: { locationId }
                })
                .then(result => {
                    this.accountingCosts = result.data;
                })
                .finally(() => {
                    this.recalculatingAccountingCosts = false;
                    if(this.accountingCosts?.length > 0) {
                        this.explanationMessage = 'The following SKUs would not be able to have their accounting cost calculated because there is insufficient or incorrect data in the Stock Ledger or the Product is currently inactive.';
                    } 
                    else {
                        this.explanationMessage = 'There are no SKUs that are exceptions for the accounting cost calculation for the current location.';
                    }
                    
                });                
        },
        async recalculateAccountingCosts() {
            this.$refs.recalculateAccountingCostModal.hide()
            this.spinnerMessage = 'Recalculating accounting costs, please wait...';
            this.recalculatingAccountingCosts = true;
            const locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            axios.post(
                '/stockledgers/applycalculateaccountingcosts', 
                { 
                    locationId  
                },
                { 
                    responseType: 'blob'
                })
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        'RecalculateAccountingCosts.csv',
                        'application/csv');

                    this.$appSuccess('Recalculate Accounting Costs CSV report file generated successfully.')
                })
                .finally(() => {
                    this.recalculatingAccountingCosts = false;                  
                });
            }
        }
    };
</script>

<style scoped lang="scss">
:deep(.noWrap ) {
    white-space: nowrap;
}
</style>
